import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface WeighScaleVisualProps {
  leftWeight: number;
  rightWeight: number;
  leftLabel?: string;
  rightLabel?: string;
  unit?: 'g' | 'kg';
  label?: string;
  mode?: 'stimulus' | 'support';
  showQuestion?: boolean;
  type?: 'balance' | 'digital';
}
export function WeighScaleVisual({
  leftWeight,
  rightWeight,
  leftLabel,
  rightLabel,
  unit = 'kg',
  label,
  mode = 'support',
  showQuestion = false,
  type = 'balance'
}: WeighScaleVisualProps) {
  const computed = useMemo(() => {
    const difference = leftWeight - rightWeight;
    const maxTilt = 20; // degrees
    const tiltAngle = Math.max(-maxTilt, Math.min(maxTilt, difference / Math.max(leftWeight, rightWeight, 1) * maxTilt));
    const isBalanced = Math.abs(difference) < 0.01;
    return {
      difference,
      tiltAngle,
      isBalanced,
      heavier: difference > 0 ? 'left' : difference < 0 ? 'right' : 'balanced'
    };
  }, [leftWeight, rightWeight]);
  const scaleViz = type === 'digital' ? <div className="flex justify-center items-center py-4" data-id="element-13419">
        <svg width="200" height="140" viewBox="0 0 200 140" className="drop-shadow-sm" data-id="element-13420">
          {/* Digital Scale Base */}
          <rect x="20" y="90" width="160" height="30" rx="8" fill="#e2e8f0" stroke="#cbd5e1" strokeWidth="2" data-id="element-13421" />
          <rect x="30" y="85" width="140" height="10" rx="2" fill="#cbd5e1" data-id="element-13422" />
          {/* Platform */}
          <path d="M 40 85 L 20 70 L 180 70 L 160 85 Z" fill="#f1f5f9" stroke="#cbd5e1" strokeWidth="1" data-id="element-13423" />
          {/* LCD Screen */}
          <rect x="60" y="95" width="80" height="20" rx="4" fill="#86efac" stroke="#4ade80" strokeWidth="1" data-id="element-13424" />
          <text x="100" y="110" textAnchor="middle" fontFamily="monospace" fontSize="14" fontWeight="bold" fill="#064e3b" data-id="element-13425">
            {showQuestion ? '?.??' : `${leftWeight} ${unit}`}
          </text>
          {/* Object on scale */}
          <rect x="70" y="30" width="60" height="40" rx="4" fill="#fde68a" stroke="#b45309" strokeWidth="2" data-id="element-13426" />
          <text x="100" y="55" textAnchor="middle" fontSize="12" fill="#78350f" fontWeight="bold" data-id="element-13427">
            {leftLabel || (showQuestion ? '?' : `${leftWeight} ${unit}`)}
          </text>
        </svg>
      </div> : <div className="flex justify-center items-center" data-id="element-13428">
        <svg width="280" height="160" viewBox="0 0 280 160" className="drop-shadow-sm" data-id="element-13429">
          {/* Base */}
          <rect x="110" y="140" width="60" height="15" rx="2" fill="#475569" stroke="#1e293b" strokeWidth="1" data-id="element-13430" />
          <rect x="120" y="130" width="40" height="10" fill="#64748b" data-id="element-13431" />

          {/* Center post */}
          <rect x="135" y="80" width="10" height="50" fill="#64748b" stroke="#475569" strokeWidth="1" data-id="element-13432" />

          {/* Balance beam (animated tilt) */}
          <motion.g initial={{
        rotate: 0
      }} animate={{
        rotate: computed.tiltAngle
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} style={{
        transformOrigin: '140px 80px'
      }} data-id="element-13433">
            {/* Beam */}
            <rect x="40" y="76" width="200" height="8" rx="4" fill="#f59e0b" stroke="#b45309" strokeWidth="1" data-id="element-13434" />

            {/* Center pivot */}
            <circle cx="140" cy="80" r="6" fill="#fbbf24" stroke="#b45309" strokeWidth="1" data-id="element-13435" />

            {/* Left chain */}
            <line x1="60" y1="80" x2="60" y2="120" stroke="#94a3b8" strokeWidth="2" data-id="element-13436" />

            {/* Right chain */}
            <line x1="220" y1="80" x2="220" y2="120" stroke="#94a3b8" strokeWidth="2" data-id="element-13437" />

            {/* Left plate */}
            <ellipse cx="60" cy="120" rx="30" ry="6" fill="#fde68a" stroke="#b45309" strokeWidth="1" data-id="element-13438" />
            <ellipse cx="60" cy="117" rx="30" ry="4" fill="#fef3c7" data-id="element-13439" />

            {/* Left weight label */}
            <text x="60" y="110" textAnchor="middle" fontSize="12" fill="#78350f" fontWeight="bold" data-id="element-13440">
              {showQuestion && !leftLabel ? '?' : leftLabel || `${leftWeight} ${unit}`}
            </text>

            {/* Right plate */}
            <ellipse cx="220" cy="120" rx="30" ry="6" fill="#fde68a" stroke="#b45309" strokeWidth="1" data-id="element-13441" />
            <ellipse cx="220" cy="117" rx="30" ry="4" fill="#fef3c7" data-id="element-13442" />

            {/* Right weight label */}
            <text x="220" y="110" textAnchor="middle" fontSize="12" fill="#78350f" fontWeight="bold" data-id="element-13443">
              {showQuestion && !rightLabel ? '?' : rightLabel || `${rightWeight} ${unit}`}
            </text>
          </motion.g>

          {/* Balance indicator */}
          {computed.isBalanced && mode === 'support' && <motion.g initial={{
        opacity: 0,
        scale: 0
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.6,
        duration: 0.4
      }} data-id="element-13444">
              <circle cx="140" cy="50" r="16" fill="#10b981" opacity="0.2" data-id="element-13445" />
              <text x="140" y="55" textAnchor="middle" fontSize="14" fill="#059669" fontWeight="bold" data-id="element-13446">
                ⚖️
              </text>
            </motion.g>}
        </svg>
      </div>;
  if (mode === 'stimulus') {
    return <div className="bg-white rounded-xl p-4 border border-slate-200 w-full max-w-sm mx-auto" data-id="element-13447">
        {label && <p className="text-sm font-semibold text-slate-700 mb-2 text-center" data-id="element-13448">
            {label}
          </p>}
        {scaleViz}
      </div>;
  }
  return <div className="bg-white rounded-xl border border-amber-100 overflow-hidden w-full max-w-sm mx-auto shadow-sm" data-id="element-13449">
      <div className="bg-amber-50 px-4 py-3 border-b border-amber-100" data-id="element-13450">
        <h3 className="text-sm font-bold text-amber-900 text-center" data-id="element-13451">
          {label || 'Weegschaal'}
        </h3>
      </div>

      <div className="p-4 bg-white" data-id="element-13452">{scaleViz}</div>

      <div className="bg-amber-50/50 p-4 border-t border-amber-50" data-id="element-13453">
        <p className="text-sm text-slate-700 text-center leading-relaxed" data-id="element-13454">
          {type === 'digital' ? <>
              De weegschaal geeft{' '}
              <strong data-id="element-13455">
                {leftWeight} {unit}
              </strong>{' '}
              aan.
            </> : computed.isBalanced ? <>
              De weegschaal is <strong data-id="element-13456">in evenwicht</strong>. Beide kanten wegen
              even zwaar.
            </> : <>
              De{' '}
              <strong data-id="element-13457">
                {computed.heavier === 'left' ? 'linker' : 'rechter'}
              </strong>{' '}
              kant is zwaarder en zakt naar beneden.
            </>}
        </p>
      </div>
    </div>;
}